/**
 * What a teacher picks from on the register (D-890).
 *
 * **Ours to write, not hers** — this is inside the signed-in app, so a literal
 * and never a `ContentBlock` (A-16 as narrowed by D-678). *"Dancer(s) are
 * unavailable"* is the one the owner asked for and the reason this exists:
 * solos, duets and trios are called off fairly regularly when a dancer cannot
 * make it, and the cover dialog's *"Nobody can take it"* is a false sentence
 * about that night.
 *
 * **Short on purpose.** A list nobody reads to the end is a list where she picks
 * the first plausible row, and the sentence Tonya reads is then wrong — which is
 * worse than no list at all. *Something else* is always offered beside these, so
 * a reason nobody anticipated never forces a wrong pick.
 */
export const CALL_OFF_REASONS = [
  'Dancer(s) are unavailable',
  'Nobody could cover it',
] as const

/**
 * What to re-read once a night has been called off from the register (D-890).
 *
 * **The caller owns this list, and the register's is not the cover dialog's.**
 * `refreshAfterDeclaring` has no calendar key, because the day dialog is never
 * opened from one — this modal always is. Without the calendar here she
 * confirms, the row behind the dialog still shows the class as on, and she has
 * just emailed every family in it (D-872, one layer up in the browser's cache
 * where `CalendarCacheInterceptor` cannot reach).
 */
export function refreshAfterCallingOff(queryClient: {
  invalidateQueries: (filters: { queryKey: readonly unknown[] }) => unknown
}): void {
  for (const key of [
    ['calendar'],
    ['roster'],
    ['sheet'],
    ['registers'],
    ['teacher-home'],
    ['teacher-day'],
    ['cover'],
    /*
     * A cancelled night leaves the conflict set — `LoadSlotsAsync` filters on
     * `Scheduled` — so calling off a clashing future night from
     * `/calendar/teachers/:date` leaves the red row standing until something
     * else happens to refetch. Found by an audit, not by anybody using it.
     */
    ['conflicts'],
    /*
     * `CallOffConfirm` already invalidates this one for the bell (D-869). It is
     * repeated here so the list reads as *everything the register's caller
     * needs* rather than leaving a reader to work out which half is covered
     * where; invalidating twice costs nothing.
     */
    ['notifications'],
  ]) {
    queryClient.invalidateQueries({ queryKey: key })
  }
}
